import React from "react";

import Grid from "@material-ui/core/Grid";

import { SubtleBadge } from "components/Badge";
import { FormCollapse } from "components/Form";
import {
  Input,
  SelectField,
  TextArea,
  withNamespace,
  withNamespaces,
} from "components/Form/FormikControls";
import { Row } from "components/Layout";
import { DescriptiveOption } from "components/SearchableSelector";

import { useSectionToggle } from "hooks";

import { DeploymentSettingsModalSection, FormColumn } from "./index";

const FinanceSubModalSection = {
  PAYMENTS: "PAYMENTS",
  RCTI: "RCTI",
  TAX_INVOICE: "TAX_INVOICE",
};

const LayoutOptions = [
  // Corresponds to agrinous.constants.py:HeaderLayoutOptions
  {
    label: "Simple Agency 1",
    value: "Simple Agency 1",
    description:
      "A basic agency template with Addressee, Logo and Document Information, in that order.",
  },
  {
    label: "Simple Agency 2",
    value: "Simple Agency 2",
    description:
      "A basic agency template with Document Information, Addressee and Logo, in that order.",
  },
  {
    label: "Simple Agency 3",
    value: "Simple Agency 3",
    description:
      "A basic agency template with Logo, Addressee and Document Information, in that order.",
  },
  {
    label: "Simple Agency 4",
    value: "Simple Agency 4",
    description:
      "A basic agency template with To business, Document Information, Logo, From business, in that order.",
  },
  {
    label: "Envelope",
    value: "Envelope",
    description: "A header template that can folded to fit in a DL Envelope.",
  },
  {
    label: "Envelope V 2",
    value: "Envelope V 2",
    description:
      "A second version header template that can folded to fit in a DL Envelope.",
  },
];

const BaseFooter = {
  label: "Base Footer",
  value: "BASE",
  description: "A default footer that contains general information.",
};

const BaseFooterV2 = {
  label: "Base Footer V 2",
  value: "BASE_V_2",
  description: "A grey agrinous logo footer with payment information",
};

const RemittanceFooter = {
  label: "Remittance Footer",
  value: "REMITTANCE",
  description:
    "A footer that contains a tear off section with payment instructions.",
};

const RemittanceFooterwithPayment = {
  label: "Remittance Footer with Payment",
  value: "REMITTANCE_WITH_PAYMENT",
  description:
    "A footer that contains a tear off section with payment instructions and payment recording.",
};

const header = title => (
  <Row>
    <SubtleBadge>{title}</SubtleBadge>
  </Row>
);

export function FinanceSection(props) {
  const { sectionHelper, ns } = props;

  const modalSection = DeploymentSettingsModalSection.FINANCE_REPORTS;
  const [section, setSection] = sectionHelper;

  const isOpen = section === modalSection;
  const onToggle = () => setSection(modalSection);

  const [subSection, setSubSection] = useSectionToggle(
    FinanceSubModalSection.RCTI,
  );

  return (
    <FormCollapse
      isOpen={isOpen}
      onToggle={onToggle}
      header={header("Finance")}
    >
      <Grid container spacing={2}>
        <FormColumn>
          <FormCollapse
            isOpen={subSection === FinanceSubModalSection.RCTI}
            onToggle={() => setSubSection(FinanceSubModalSection.RCTI)}
            header={header("RCTI")}
          >
            <Grid item xs={12} md={6}>
              <Input
                label="Account Sale/RCTI Prefix"
                name={withNamespace(ns, "accountSalePrefix")}
                tooltip="The prefix used for Account Sale/RCTI documents.  EG RCTI-1234"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                label="Next Account Sale/RCTI Number"
                name={withNamespace(ns, "nextAccountSaleNumber")}
                tooltip="The next Account Sale/RCTI document created by your organization will use this number, combined with your current Account Sale/RCTI Prefix."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                label="Primary Color"
                name={withNamespaces([ns, "rctiSettings"], "primaryColor")}
                type="color"
                tooltip="This colour is used in the header of several reports."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                label="Secondary Color"
                name={withNamespaces([ns, "rctiSettings"], "secondaryColor")}
                type="color"
                tooltip="This colour is used in the header of several reports."
              />
            </Grid>
            <Grid item xs={12}>
              <SelectField
                label="Header Layout"
                name={withNamespaces([ns, "rctiSettings"], "headerLayout")}
                options={LayoutOptions}
                tooltip="Select the header layout of the document"
                components={{ Option: DescriptiveOption }}
              />
              <SelectField
                label="Footer Layout"
                name={withNamespaces([ns, "rctiSettings"], "footerLayout")}
                options={[BaseFooter, BaseFooterV2, RemittanceFooter]}
                tooltip="Select the footer layout of the logo on the document"
                components={{ Option: DescriptiveOption }}
              />
            </Grid>
          </FormCollapse>
          <FormCollapse
            isOpen={subSection === FinanceSubModalSection.TAX_INVOICE}
            onToggle={() => setSubSection(FinanceSubModalSection.TAX_INVOICE)}
            header={header("Tax Invoice")}
          >
            <Grid item xs={12} md={6}>
              <Input
                label="Invoice Prefix"
                name={withNamespace(ns, "invoicePrefix")}
                tooltip="The prefix used for Invoice documents. EG INV-1234"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                label="Next Invoice Number"
                name={withNamespace(ns, "nextInvoiceNumber")}
                tooltip="The next Invoice document created by your organization will use this number, combined with your current Invoice Prefix."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                label="Primary Color"
                name={withNamespaces(
                  [ns, "taxInvoiceSettings"],
                  "primaryColor",
                )}
                type="color"
                tooltip="This colour is used in the header of several reports."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                label="Secondary Color"
                name={withNamespace(
                  `${ns}.taxInvoiceSettings`,
                  "secondaryColor",
                )}
                type="color"
                tooltip="This colour is used in the header of several reports."
              />
            </Grid>
            <Grid item xs={12}>
              <SelectField
                label="Header Layout"
                name={withNamespaces(
                  [ns, "taxInvoiceSettings"],
                  "headerLayout",
                )}
                options={LayoutOptions}
                tooltip="Select the header layout of the logo on the document"
                components={{ Option: DescriptiveOption }}
              />
              <SelectField
                label="Footer Layout"
                name={withNamespaces(
                  [ns, "taxInvoiceSettings"],
                  "footerLayout",
                )}
                options={[
                  BaseFooter,
                  BaseFooterV2,
                  RemittanceFooter,
                  RemittanceFooterwithPayment,
                ]}
                tooltip="Select the footer layout of the logo on the document"
                components={{ Option: DescriptiveOption }}
              />
            </Grid>
          </FormCollapse>
          <FormCollapse
            isOpen={subSection === FinanceSubModalSection.PAYMENTS}
            onToggle={() => setSubSection(FinanceSubModalSection.PAYMENTS)}
            header={header("Payments")}
          >
            <Grid item xs={12} md={12}>
              <TextArea
                label="Payment Directions"
                name={withNamespace(ns, "paymentDirections")}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Input
                label="BPay Biller Code"
                name={withNamespace(ns, "bpayBillerCode")}
                maxLength={10}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Input
                label="BPay Short Name"
                name={withNamespace(ns, "bpayBillerShortName")}
                disabled
                tooltip="BPay short name is automatically filled after adding a Biller Code."
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Input
                label="BPay Long Name"
                name={withNamespace(ns, "bpayBillerLongName")}
                disabled
                tooltip="BPay long name is automatically filled after adding a Biller Code."
              />
            </Grid>
          </FormCollapse>
        </FormColumn>
      </Grid>
    </FormCollapse>
  );
}
